import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useEffect, useMemo } from 'react';

import type { IVariablesData } from '../../WorkOrderUseCoefficient';
import {
    uerWorkOrderPriceListRatioForObjects,
    uerWorkOrderPriceListRatioForRootObject,
} from '../GridPickCoefficientsQueries';

export const useWorkOrderGridPickData = (variablesData?: IVariablesData) => {
    const { isRootItem, workOrderObjectIds } = variablesData || {};

    const filteredWorkOrderObjectIds = useMemo(() => {
        if (!Array.isArray(workOrderObjectIds)) {
            return workOrderObjectIds;
        }

        return workOrderObjectIds.filter((item) => item !== null);
    }, [workOrderObjectIds]);

    const variablesForRootObject = useMemo(
        () => ({
            workOrderObjectId: filteredWorkOrderObjectIds,
        }),
        [filteredWorkOrderObjectIds],
    );

    const variablesForObjects = useMemo(
        () => ({
            workOrderObjectIds: filteredWorkOrderObjectIds,
        }),
        [filteredWorkOrderObjectIds],
    );

    const [priceListForRootObjectData, priceListForRootObjectStatus, , priceListForRootObjectRequest] = useRequest({
        requestId: 'priceListRatioForRootObject',
        query: uerWorkOrderPriceListRatioForRootObject,
        withEffect: false,
        variables: variablesForRootObject,
    });

    const [priceListForObjectsData, priceListForObjectsStatus, , priceListForObjectRequest] = useRequest({
        requestId: 'priceListRatioForObjects',
        query: uerWorkOrderPriceListRatioForObjects,
        withEffect: false,
        variables: variablesForObjects,
    });

    const orderFetchingStatus = useMemo(
        () => (isRootItem && priceListForRootObjectStatus) || (!isRootItem && priceListForObjectsStatus),
        [isRootItem, priceListForRootObjectStatus, priceListForObjectsStatus],
    );

    const requestId = isRootItem ? 'priceListRatioForRootObject' : 'priceListRatioForObjects';
    const requestFn = isRootItem ? priceListForRootObjectRequest : priceListForObjectRequest;
    const variables = isRootItem ? variablesForRootObject : variablesForObjects;

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: requestFn as any,
        requestVariables: variables,
    });

    useEffect(() => {
        if (priceListForRootObjectData?.uerWorkOrderPriceListRatioForRootObject?.items && getRowParams?.current) {
            const items = priceListForRootObjectData?.uerWorkOrderPriceListRatioForRootObject?.items;

            getRowParams?.current?.successCallback(
                items,
                priceListForRootObjectData?.uerWorkOrderPriceListRatioForRootObject?.totalCount,
            );
        }

        if (priceListForObjectsData?.uerWorkOrderPriceListRatioForObjects?.items && getRowParams?.current) {
            const items = priceListForObjectsData?.uerWorkOrderPriceListRatioForObjects?.items;

            getRowParams?.current?.successCallback(
                items,
                priceListForObjectsData?.uerWorkOrderPriceListRatioForObjects?.totalCount,
            );
        }
    }, [
        getRowParams,
        priceListForObjectsData?.uerWorkOrderPriceListRatioForObjects?.items,
        priceListForObjectsData?.uerWorkOrderPriceListRatioForObjects?.totalCount,
        priceListForRootObjectData?.uerWorkOrderPriceListRatioForRootObject?.items,
        priceListForRootObjectData?.uerWorkOrderPriceListRatioForRootObject?.totalCount,
    ]);

    return {
        orderDatasource: dataSource,
        orderFetchingStatus,
    };
};
